import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { DayRangeField } from "../components/DayRangeField";
import { UploadMultiImages } from "@/components/ui/upload-multi-images";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { Link, MapPin, Euro, ArrowRight } from "lucide-react";
import { ActivityActivity } from "../models/activity.model";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import type { DayAxis } from "../models/day-axis";
import ActivityDetailView from "./ActivityDetailView";

type ActivityTab = 'details' | 'media' | 'documents';

const MEDIA_ERROR_FIELDS = ['media'] as const;

interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<ActivityActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<ActivityActivity>;
  itineraryId: string;
  // optional itinerary bounds (may be passed by caller)
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  /** Asse dei giorni: sui template si sceglie "Giorno N" invece di una data. */
  axis?: DayAxis | null;
  defaultDate?: string;
  /** Nasconde la card data e la rende non obbligatoria (usato dalla Libreria). */
  hideDates?: boolean;
  /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
  readOnly?: boolean;
  /** Il cliente ha gia' pagato: costo e "includi nel prezzo" sono consolidati. */
  pricingLocked?: boolean;
}

const ActivityModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate, axis, defaultDate, hideDates = false, readOnly = false, pricingLocked = false }: ActivityModalProps) => {
  const { t } = useI18n();

  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<ActivityActivity>>({
    id: activityId,
    include_price: true,
    title: '',
    link: '',
    address: '',
    start_date: undefined,
    end_date: undefined,
    start_time: '',
    end_time: '',
    cost: 0,
    media: [],
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<ActivityTab>('details');

  useEffect(() => {
    if (!isOpen) {
      setShowValidationBanner(false);
      setErrors({});
      setActiveTab('details');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !initialData && defaultDate) {
      setFormData(prev => ({ ...prev, start_date: defaultDate, end_date: defaultDate }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validateForm = (): Record<string, boolean> => {
    const requiredFields = hideDates
      ? ['title', 'address', 'start_time', 'end_time', 'cost', 'media']
      : ['title', 'address', 'start_date', 'end_date', 'start_time', 'end_time', 'cost', 'media'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof ActivityActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || (value as number) < 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (!Object.values(newErrors).some(error => error)) {
      onSave({
        ...formData,
        type: 'activity' as const
      });
      onClose();
      return;
    }

    setShowValidationBanner(true);
    // Porta l'utente sulla tab del primo errore, altrimenti resta invisibile
    if (MEDIA_ERROR_FIELDS.some(field => newErrors[field])) {
      setActiveTab('media');
    } else {
      setActiveTab('details');
    }
  };

  // derive min/max for date picker: prefer explicit props, then common fields on initialData
  const derivedMinDate: string | null =
    itineraryStartDate
    || (initialData as any)?.itineraryStartDate
    || (initialData as any)?.itinerary_start_date
    || (initialData as any)?.min_date
    || (initialData as any)?.minDate
    || null;

  const derivedMaxDate: string | null =
    itineraryEndDate
    || (initialData as any)?.itineraryEndDate
    || (initialData as any)?.itinerary_end_date
    || (initialData as any)?.max_date
    || (initialData as any)?.maxDate
    || null;

  const hasDetailsError = ['title', 'address', 'start_date', 'end_date', 'start_time', 'end_time', 'cost']
    .some(field => errors[field]);
  const hasMediaError = MEDIA_ERROR_FIELDS.some(field => errors[field]);

  if (readOnly) {
    return (
      <ActivityDetailView
        isOpen={isOpen}
        onClose={onClose}
        icon={icon}
        title={t('builder.activities.activity')}
        activity={formData}
      />
    );
  }

  return (
    <ActivityModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      title={t('builder.activities.activity')}
      formId="activity-form"
      onSubmit={handleSubmit}
      showValidationBanner={showValidationBanner}
      activeTab={activeTab}
      onActiveTabChange={(value) => setActiveTab(value as ActivityTab)}
      tabs={[
        {
          key: 'details',
          label: t('modals.tabDetails'),
          hasError: hasDetailsError,
          content: (
            <>
              {/* General Information */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('modals.generalInformation')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Input
                        id="title"
                        value={formData.title || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        placeholder={t('modals.activityTitle')}
                        hasError={errors.title}
                        readOnly={readOnly}
                      />
                    </div>
                    <div>
                      <div className="relative">
                        <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                        <Input
                          id="link"
                          type="url"
                          value={formData.link || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                          placeholder={t('modals.activityLink')}
                          className="pl-10"
                          hasError={errors.link}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                      <Input
                        id="address"
                        value={formData.address || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        placeholder={t('modals.address')}
                        className="pl-10"
                        hasError={errors.address}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Date Range and Time Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Date Range Card */}
                {!hideDates && (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('modals.from')} - {t('modals.to')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <DayRangeField
                        axis={axis}
                        value={formData}
                        onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                        hasError={errors.start_date || errors.end_date}
                        minDate={derivedMinDate || undefined}
                        maxDate={derivedMaxDate || undefined}
                        disabled={readOnly}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Time Range Card */}
                <Card className={hideDates ? 'sm:col-span-2' : undefined}>
                  <CardHeader>
                    <CardTitle>{t('modals.time')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-row justify-evenly items-center">
                      <div className="flex flex-row items-center">
                        <Input
                          className="w-30"
                          type="time"
                          value={formData.start_time || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                          placeholder={t('common.from')}
                          hasError={errors.start_time}
                          readOnly={readOnly}
                        />
                      </div>
                      <span>
                        <ArrowRight className="mx-2 h-4 w-4 text-gray-500" />
                      </span>
                      <div className="flex flex-row items-center">
                        <Input
                          className="w-30"
                          type="time"
                          value={formData.end_time || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                          placeholder={t('common.to')}
                          hasError={errors.end_time}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Price Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('modals.price')}</CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{t('modals.includePrice')}</span>
                      <Switch
                        checked={formData.include_price || false}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                        disabled={readOnly || pricingLocked}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                        <Input
                          id="cost"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.cost}
                          onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                          placeholder={t('modals.amountOnlyVisibleToYou')}
                          className="pl-10"
                          hasError={errors.cost}
                          readOnly={readOnly || pricingLocked}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Description Card */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('modals.description')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <RichTextEditor
                    value={formData.description || ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    placeholder={t('modals.describeActivity')}
                    readOnly={readOnly}
                  />
                </CardContent>
              </Card>
            </>
          ),
        },
        {
          key: 'media',
          label: t('modals.tabMedia'),
          hasError: hasMediaError,
          content: (
            <Card>
              <CardHeader>
                <CardTitle>{t('modals.media')}</CardTitle>
              </CardHeader>
              <CardContent>
                <UploadMultiImages
                  images={formData.media || []}
                  onImagesChange={(images) => setFormData(prev => ({ ...prev, media: images }))}
                  label={t('modals.upload4Photos')}
                  maxImages={10}
                  hasError={errors.media}
                  itineraryId={itineraryId}
                  activityId={activityId}
                  readOnly={readOnly}
                />
              </CardContent>
            </Card>
          ),
        },
        {
          key: 'documents',
          label: t('modals.tabDocuments'),
          content: (
            <Card>
              <CardHeader>
                <CardTitle>{t('modals.documentation')}</CardTitle>
              </CardHeader>
              <CardContent>
                <UploadMultiFiles
                  files={formData.documentation || []}
                  onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                  label={t('modals.uploadBookingDocumentation')}
                  maxFiles={5}
                  itineraryId={itineraryId}
                  activityId={activityId}
                  readOnly={readOnly}
                />
              </CardContent>
            </Card>
          ),
        },
      ]}
    />
  );
};

export default ActivityModal;
